import type { DayRecord } from "../../shared/types";
import { addDays } from "../../shared/time";
import { shortDate } from "../format";
import { StarIcon } from "./StarIcon";

export const GRID_DAYS = 28;

type Cell = { day: string; kind: "star" | "miss" | "open" | "none" };

/**
 * The last four weeks, oldest top-left and today bottom-right: a star for a star day, an empty
 * circle for a closed day without one, a faint dot for a day not closed yet (only ever today:
 * every earlier day closes by itself at midnight), and nothing before the kid was added. Whole weeks from
 * before then are left out rather than drawn blank.
 */
export function StarGrid({ days, today, firstDay }: { days: DayRecord[]; today: string; firstDay: string }) {
  const closed = new Map(days.map((d) => [d.day, d.star]));
  const cells: Cell[] = Array.from({ length: GRID_DAYS }, (_, i) => {
    const day = addDays(today, i - (GRID_DAYS - 1));
    const star = closed.get(day);
    const kind = day < firstDay ? "none" : star === undefined ? "open" : star ? "star" : "miss";
    return { day, kind };
  });
  const words = { star: "star", miss: "no star", open: "not closed yet", none: "" };
  const emptyWeeks = Math.floor(cells.findIndex((c) => c.kind !== "none") / 7);

  return (
    <ol className="star-grid" aria-label="The last four weeks">
      {cells.slice(emptyWeeks * 7).map((c) => (
        <li key={c.day} className={`star-grid__cell star-grid__cell--${c.kind}`} aria-current={c.day === today ? "date" : undefined}>
          {c.kind === "star" && <StarIcon className="star-grid__star" />}
          {c.kind !== "none" && <span className="visually-hidden">{`${shortDate(c.day)}: ${words[c.kind]}`}</span>}
        </li>
      ))}
    </ol>
  );
}
